import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useKeycloak } from "@react-keycloak/web";
import {
  BrandPage,
  Button,
  PageSpinner,
  Spinner,
} from "@digitalisation/enlixy-comp";
import { useUnifiedAuth } from "../auth/useUnifiedAuth";

/**
 * Shows the app once Keycloak is initialized and the user is signed in;
 * until then the shared sign-in spinner, or the redirect page with a manual
 * login button. Both carry the organization's branding of the last visit.
 */
const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { initialized, authenticated, login } = useUnifiedAuth();
  const { keycloak } = useKeycloak();
  const loginTriggered = useRef(false);
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Automatically trigger login when user is not authenticated
  useEffect(() => {
    if (initialized && !authenticated && !loginTriggered.current) {
      loginTriggered.current = true;
      console.log("🔐 Auto-triggering login since user is not authenticated");

      // Try the login() method first
      login();

      // If login() doesn't redirect within 3 seconds, force redirect
      redirectTimer.current = setTimeout(async () => {
        if (!authenticated && keycloak) {
          console.log(
            "🔐 Login method did not redirect, forcing Keycloak login URL redirect",
          );
          const loginUrl = await keycloak.createLoginUrl({
            redirectUri: window.location.origin,
          });
          window.location.href = loginUrl;
        }
      }, 3000);
    }

    return () => {
      if (redirectTimer.current) {
        clearTimeout(redirectTimer.current);
      }
    };
  }, [initialized, authenticated, login, keycloak]);

  // Clear timer if user becomes authenticated (above the early returns:
  // hooks run in the same order on every render)
  useEffect(() => {
    if (authenticated && redirectTimer.current) {
      clearTimeout(redirectTimer.current);
    }
  }, [authenticated]);

  if (!initialized) {
    return <PageSpinner label={t("auth.initializingAuthentication")} />;
  }

  if (!authenticated) {
    return (
      <BrandPage product="Performance Cockpit">
        <div className="rounded-[20px] bg-surface p-6 text-center shadow-panel">
          <Spinner size={32} className="text-accent" />
          <h1 className="mt-4 text-lg font-semibold text-fg">
            Redirecting to Login
          </h1>
          <p className="mt-2 text-sm text-fg-3">
            Redirecting to Keycloak login page...
          </p>
          <p className="mt-4 text-xs text-muted">
            If you're not redirected automatically:
          </p>
          <Button
            variant="primary"
            className="mt-3"
            onClick={async () => {
              if (keycloak) {
                const loginUrl = await keycloak.createLoginUrl({
                  redirectUri: window.location.origin,
                });
                window.location.href = loginUrl;
              } else {
                login();
              }
            }}
          >
            Click here to Login
          </Button>
        </div>
      </BrandPage>
    );
  }

  return <>{children}</>;
};

export default AuthGuard;
